import { useEffect, useRef, useState } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Modal from "modules/Modal/Modal";
import { CreateOrganizationAddressInput, Organization } from "generated/graphql";
import useCreateOrganizationAddress from "hooks/organization/useCreateOrganizationAddress";
import OrganizationInfo, {
  OrganizationInfoHandle,
} from "modules/organization/UpdateOrganization/OrganizationInfo";
import Create from "modules/OrganizationAddresses/Create";
import PageTour, { PageTourHandle } from "components/PageTour/PageTour";
import trialUserOrganizationSettingsTourSteps from "./TrialUserOrganizationSettings.tour";

type TrialUserOrganizationSettingsProps = {
  open: boolean;
  onClose: () => void;
  organization: Organization;
  /** Called once the organization address has been saved successfully. */
  onComplete?: () => void;
  /** Position of this tour's first step within a larger multi-tour flow. */
  stepOffset?: number;
  /** Total step count across a larger multi-tour flow. */
  totalSteps?: number;
};

export default function TrialUserOrganizationSettings({
  open,
  onClose,
  organization,
  onComplete,
  stepOffset,
  totalSteps,
}: TrialUserOrganizationSettingsProps) {
  const [createOrganizationAddress, { loading, isSuccess, getData }] =
    useCreateOrganizationAddress();
  const tourRef = useRef<PageTourHandle>(null);
  const orgInfoRef = useRef<OrganizationInfoHandle>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timezoneMenuOpen, setTimezoneMenuOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Defer until after the modal's content has mounted and painted, otherwise
    // Joyride can try to position against targets that aren't in the DOM yet.
    const timeout = setTimeout(() => tourRef.current?.replay(), 50);
    return () => clearTimeout(timeout);
  }, [open]);

  const onSubmitAddress = async (input: CreateOrganizationAddressInput) => {
    setIsSubmitting(true);
    try {
      const [result] = await Promise.all([
        createOrganizationAddress({ variables: { input } }),
        orgInfoRef.current?.submit(),
      ]);
      const success = isSuccess(result.data);
      const data = getData(result);
      if (success) onComplete?.();
      return { success, data };
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} styles={{ maxHeight: "90vh", overflowY: "auto" }}>
      <PageTour
        ref={tourRef}
        tourKey="trialUserOrganizationSettings"
        steps={trialUserOrganizationSettingsTourSteps}
        stepOffset={stepOffset}
        totalSteps={totalSteps}
        onStepChange={(step) => setTimezoneMenuOpen(step.target === ".tour-organization-timezone")}
      />
      <MDTypography variant="h6" p={3} pb={0}>
        Organization Settings
      </MDTypography>
      <MDBox p={3} display="flex" flexDirection="column" gap="16px">
        <OrganizationInfo
          ref={orgInfoRef}
          organization={organization}
          showContactFields={false}
          showSaveButton={false}
          onLogoUploaded={() => tourRef.current?.next()}
          openTimezoneSelect={timezoneMenuOpen}
          onTimezoneSelected={() => tourRef.current?.next()}
        />
        <Create
          onSubmit={onSubmitAddress}
          loading={loading || isSubmitting}
          onAddressSelected={() => tourRef.current?.next()}
          submitLabel="Save"
          defaultIsPrimary={true}
        />
      </MDBox>
    </Modal>
  );
}
